import React, { memo } from 'react';
import { Box, Divider, Skeleton, Stack } from '@mui/material';
import { drawerWidth } from '@/constants';

const MENU_ITEMS_WITH_CHEVRON = [1, 3, 4, 5, 6, 7, 9, 10, 11];
const ACTIVITY_COUNT = 7;
const MENU_ITEM_COUNT = 12;

const ModuleSkeleton = memo(() => {
	return (
		<Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
			{/* ── Sidebar ── */}
			<Box
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					display: 'flex',
					flexDirection: 'column',
					borderRight: '1px solid',
					borderColor: 'divider',
					px: 1.5,
					py: 1.5,
				}}
			>
				{/* Logo */}
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 1 }}>
					<Skeleton variant="rounded" width={148} height={38} />
					<Divider sx={{ width: '100%' }} flexItem />
					{/* Search */}
					<Skeleton variant="rounded" height={28} width={'100%'}/>
					<Divider sx={{ width: '100%' }} flexItem/>
				</Box>

				{/* Nav menu items */}
				{Array.from({ length: MENU_ITEM_COUNT }).map((_, i) => (
					<Stack
						key={i}
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{ py: 0.75, px: 1 }}
					>
						<Stack direction="row" alignItems="center" gap={1.5}>
							<Skeleton
								variant="rounded"
								width={20}
								height={20}
								sx={{ borderRadius: 0.75, flexShrink: 0 }}
							/>
							<Skeleton
								variant="text"
								height={20}
								sx={{ width: 90 + (i % 4) * 18 }}
							/>
						</Stack>
						{MENU_ITEMS_WITH_CHEVRON.includes(i) && (
							<Skeleton variant="rounded" width={14} height={14} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
						)}
					</Stack>
				))}

				{/* Footer */}
				<Box sx={{ mt: 'auto', pt: 2, textAlign: 'center' }}>
					<Skeleton variant="text" width={160} height={16} sx={{ mx: 'auto' }} />
				</Box>
			</Box>

			{/* ── Right: Appbar + Page ── */}
			<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				{/* Appbar */}
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						px: 2.5,
						py: 1.25,
						borderBottom: '1px solid',
						borderColor: 'divider',
						flexShrink: 0,
						minHeight: 56,
					}}
				>
					<Stack direction="row" alignItems="center" gap={1.5}>
						<Skeleton variant="rounded" width={22} height={22} sx={{ borderRadius: 0.5 }} />
						<Skeleton variant="rounded" width={22} height={22} sx={{ borderRadius: 0.5 }} />
						<Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: 2 }} />
					</Stack>
					<Stack direction="row" alignItems="center" gap={1.5}>
						<Skeleton variant="circular" width={28} height={28} />
						<Skeleton variant="circular" width={38} height={38} />
						<Box>
							<Skeleton variant="text" width={108} height={20} />
							<Skeleton variant="text" width={68} height={16} />
						</Box>
					</Stack>
				</Stack>

				{/* Page content + Right panel */}
				<Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
					{/* ── Main page area ── */}
					<Box sx={{ flex: 1, p: 3, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
						{/* Page title row */}
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{ mb: 3, flexShrink: 0 }}
						>
							<Skeleton variant="text" width={160} height={40} />
							<Stack direction="row" alignItems="center" gap={1}>
								<Skeleton variant="rounded" width={122} height={34} sx={{ borderRadius: 1.5 }} />
								<Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 1.5 }} />
							</Stack>
						</Stack>

						{/* Content block */}
						<Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 3, minHeight: 320 }} />
					</Box>

					{/* ── Right panel ── */}
					<Box
						sx={{
							width: 260,
							flexShrink: 0,
							borderLeft: '1px solid',
							borderColor: 'divider',
							p: 2,
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							overflow: 'auto',
						}}
					>
						{/* Add Records card */}
						<Box
							sx={{
								border: '2px dashed',
								borderColor: 'divider',
								borderRadius: 2,
								p: 3,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								gap: 2,
							}}
						>
							<Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: 1.5 }} />
							<Skeleton variant="rounded" width={130} height={36} sx={{ borderRadius: 1.5 }} />
						</Box>

						{/* Activities heading */}
						<Skeleton variant="text" width={90} height={26} />

						{/* Activity list */}
						{Array.from({ length: ACTIVITY_COUNT }).map((_, i) => (
							<Stack key={i} direction="row" alignItems="flex-start" gap={1.5}>
								<Skeleton
									variant="circular"
									width={8}
									height={8}
									sx={{ mt: 0.75, flexShrink: 0 }}
								/>
								<Box sx={{ flex: 1 }}>
									<Skeleton variant="text" width="88%" height={18} />
									<Skeleton variant="text" width={86} height={14} />
								</Box>
							</Stack>
						))}
					</Box>
				</Box>
			</Box>
		</Box>
	);
});

ModuleSkeleton.displayName = 'ModuleSkeleton';

export { ModuleSkeleton };
export default ModuleSkeleton;
